// @erp/ui/hub-grid — the card grid a console's LANDING page is made of.
//
// Seven landings drew this by hand (the lib consoles, the CMS console, the
// staff desk), each the same four lines: a <Link> wrapping a Card, a CardHeader
// for the title, a separately padded body, and a green `StatusBadge label="Open"`
// standing in for a button. Two things were wrong with that beyond the
// duplication:
//
//   • A STATUS BADGE IS NOT A BUTTON. It is the component that says what state a
//     record is IN, and `tone="success"` is the green that means "fine" — spent
//     here on every card of every landing, where nothing has a state at all.
//   • THE BANDS. A header rule and a second padded box drew three frames around
//     a title, a line of copy and one verb (the owner, 2026-10-02: "you don't
//     need header footer in cards always").
//
// It is `CardTile` now — one padded box — with the module's hue on the mark and
// a real blue verb at the foot, which is the treatment the ERP home and Adobe's
// own product cards use.
import { For, type Component } from 'solid-js';
import { CardTile } from '../card/index.js';
import { LinkButton } from '../link-button/index.js';
import type { CategoryHue } from '../category-hue/index.js';

export interface HubLink {
  /** Where the card opens. */
  to: string;
  /** The destination's name — the tile's title. */
  label: string;
  /** One line under it: what that screen is for. */
  blurb: string;
  /** Automation id root: the tile is `${id}-card`, its button `${id}-open`. */
  id: string;
  /** A lucide-solid component for the tile's mark; without one the tile shows none. */
  icon?: Component<{ size?: number; class?: string }>;
}

export function HubGrid(props: {
  items: readonly HubLink[];
  /** The console's identity hue — every tile on one landing wears the same one. */
  hue?: CategoryHue | 'neutral';
  /** The verb on each tile (default "Open"). */
  verb?: string;
  /** Automation id on the grid. */
  id?: string;
  /** Column count from `lg` (default 3, the width Adobe's own card grid uses). */
  columns?: 2 | 3 | 4;
}) {
  const cols = () =>
    props.columns === 2 ? 'lg:grid-cols-2' : props.columns === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3';
  return (
    <div id={props.id} class={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${cols()}`}>
      <For each={props.items}>
        {(link) => (
          <CardTile
            id={`${link.id}-card`}
            {...(link.icon ? { icon: link.icon, hue: props.hue } : {})}
            title={link.label}
            description={link.blurb}
            actions={
              <LinkButton id={`${link.id}-open`} to={link.to} variant="primary" size="s">
                {props.verb ?? 'Open'}
              </LinkButton>
            }
          />
        )}
      </For>
    </div>
  );
}
